/**
 * The dialogs the contacts screen opens: the New/Edit Contact form (with a
 * live duplicate check as you type — the reason `contacts.routes.js` leaves
 * `id` un-uuid-validated and accepts the literal `new`), delete confirmation,
 * and the explanation dialog for header actions §5 documents but does not
 * implement (an org-wide duplicate inbox has no batch endpoint behind it; only
 * the per-record check this form already uses).
 */
import { useEffect, useMemo, useState } from 'react';
import { Modal, Field } from '../../components/common/index.jsx';
import { ButtonSpinner } from '../../components/common/Loading.jsx';
import { useDebounce } from '../../hooks/useDebounce.js';
import { useContactDuplicates } from './useContacts.js';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const BUYING_ROLE = ['Economic buyer', 'Champion', 'Blocker', 'Technical DM', 'Buyer', 'Influencer', 'End user', 'Gatekeeper'];
const INFLUENCE = ['Low', 'Medium', 'High'];
const PREFERRED_CHANNEL = ['Email', 'Phone', 'WhatsApp', 'Teams', 'LinkedIn', 'In person'];

const BLANK = {
  salutation: '', first_name: '', last_name: '', job_title: '', account_id: '',
  email: '', phone: '', mobile: '', linkedin_url: '', preferred_channel: '',
  buying_role: '', influence: '', is_primary: false,
  birthday: '', joined_company_on: '', customer_since: '',
};

function validateContact(form) {
  const errors = {};
  if (!form.first_name.trim()) errors.first_name = 'First name is required.';
  if (!form.last_name.trim()) errors.last_name = 'Last name is required.';
  if (form.email && !EMAIL_RE.test(form.email)) errors.email = 'Enter a valid email address.';
  return errors;
}

/**
 * Create a contact, or edit one. `contact` (a `contactShape.toRow()` row)
 * switches the mode. While `last_name`/`email`/`phone` change, a debounced
 * `GET /contacts/{id|new}/duplicates` runs in the background — a real
 * suggestion, not a fake one, since the same signals the server checks here
 * are what a bulk scan would use too.
 */
export function ContactFormModal({ open, onClose, onSubmit, onDelete, busy, options, contact }) {
  const [form, setForm] = useState(BLANK);
  const [errors, setErrors] = useState({});
  const [dismissedDupes, setDismissedDupes] = useState(false);
  const editing = Boolean(contact);

  useEffect(() => {
    if (!open) return;
    if (contact) {
      setForm({
        salutation: contact.salutation ?? '',
        first_name: contact.firstName ?? '',
        last_name: contact.lastName ?? '',
        job_title: contact.jobTitle ?? '',
        account_id: contact.account?.id ?? '',
        email: contact.email ?? '',
        phone: contact.phone ?? '',
        mobile: contact.mobile ?? '',
        linkedin_url: contact.linkedinUrl ?? '',
        preferred_channel: contact.preferredChannel ?? '',
        buying_role: contact.buyingRole ?? '',
        influence: contact.influence ?? '',
        is_primary: Boolean(contact.isPrimary),
        birthday: contact.birthday ?? '',
        joined_company_on: contact.joinedCompanyOn ?? '',
        customer_since: contact.customerSince ?? '',
      });
    } else {
      setForm(BLANK);
    }
    setErrors({});
    setDismissedDupes(false);
  }, [open, contact]);

  const set = (k) => (e) => {
    const { value, type, checked } = e.target;
    setForm((f) => ({ ...f, [k]: type === 'checkbox' ? checked : value }));
    setErrors((er) => (er[k] ? { ...er, [k]: undefined } : er));
    if (['last_name', 'email', 'phone'].includes(k)) setDismissedDupes(false);
  };

  const debouncedCandidate = useDebounce(
    useMemo(() => ({
      last_name: form.last_name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      account_id: form.account_id || undefined,
    }), [form.last_name, form.email, form.phone, form.account_id]),
    400,
  );
  const hasSignal = Boolean(debouncedCandidate.email || debouncedCandidate.phone
    || (debouncedCandidate.last_name && debouncedCandidate.account_id));
  const { data: dupeData } = useContactDuplicates(
    contact?.id ?? 'new',
    debouncedCandidate,
    open && hasSignal,
  );
  const dupes = (dupeData?.matches ?? []).filter((m) => m.id !== contact?.id);

  const submit = (e) => {
    e.preventDefault();
    const nextErrors = validateContact(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    const payload = {};
    for (const [k, v] of Object.entries(form)) {
      if (k === 'is_primary') { payload[k] = v; continue; }
      if (v === '' || v == null) continue;
      payload[k] = v;
    }
    onSubmit(payload);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit Contact' : 'New Contact'}
      size="lg"
      footer={(
        <>
          {editing && onDelete && (
            <button
              type="button" className="btn btn-delete btn-sm-x me-auto"
              onClick={onDelete}
            >
              <i className="bi bi-trash3 me-1" aria-hidden="true" />Delete
            </button>
          )}
          <button type="button" className="btn btn-cancel btn-sm-x" onClick={onClose}>Cancel</button>
          <button type="submit" form="contactForm" className="btn btn-save btn-sm-x" disabled={busy}>
            {busy ? <><ButtonSpinner />Saving…</> : editing ? 'Save changes' : 'Save contact'}
          </button>
        </>
      )}
    >
      <form id="contactForm" onSubmit={submit} noValidate>
        {!editing && dupes.length > 0 && !dismissedDupes && (
          <div className="alert alert-warning d-flex align-items-start gap-2 mb-3" role="alert">
            <i className="bi bi-exclamation-triangle-fill mt-1" aria-hidden="true" />
            <div className="flex-grow-1">
              <div className="fw-650 t-sm mb-1">Possible duplicate{dupes.length > 1 ? 's' : ''}</div>
              <ul className="t-xs mb-0 ps-3">
                {dupes.slice(0, 3).map((m) => (
                  <li key={m.id}>
                    <strong>{m.full_name}</strong>{m.account ? ` · ${m.account}` : ''}
                    {m.email ? ` · ${m.email}` : ''} — matched on {m.match_on.join(', ')}
                  </li>
                ))}
              </ul>
            </div>
            <button type="button" className="btn-close btn-sm" aria-label="Dismiss" onClick={() => setDismissedDupes(true)} />
          </div>
        )}

        <div className="eyebrow mb-2">Person</div>
        <div className="row g-3 mb-3">
          <div className="col-md-2">
            <Field label="Salutation"><input className="form-control" value={form.salutation} onChange={set('salutation')} /></Field>
          </div>
          <div className="col-md-5">
            <Field label="First name" required error={errors.first_name}>
              <input className="form-control" value={form.first_name} onChange={set('first_name')} />
            </Field>
          </div>
          <div className="col-md-5">
            <Field label="Last name" required error={errors.last_name}>
              <input className="form-control" value={form.last_name} onChange={set('last_name')} />
            </Field>
          </div>
          <div className="col-md-6">
            <Field label="Job title"><input className="form-control" value={form.job_title} onChange={set('job_title')} /></Field>
          </div>
          <div className="col-md-6">
            <Field label="Account" help="Their primary employer.">
              <Picker value={form.account_id} onChange={set('account_id')} options={options.accounts} blank="No account" />
            </Field>
          </div>
        </div>

        <div className="eyebrow mb-2">Contact details</div>
        <div className="row g-3 mb-3">
          <div className="col-md-6">
            <Field label="Email" error={errors.email}>
              <input className="form-control" type="email" value={form.email} onChange={set('email')} />
            </Field>
          </div>
          <div className="col-md-3">
            <Field label="Phone"><input className="form-control" value={form.phone} onChange={set('phone')} /></Field>
          </div>
          <div className="col-md-3">
            <Field label="Mobile"><input className="form-control" value={form.mobile} onChange={set('mobile')} /></Field>
          </div>
          <div className="col-md-6">
            <Field label="LinkedIn URL"><input className="form-control" type="url" placeholder="https://…" value={form.linkedin_url} onChange={set('linkedin_url')} /></Field>
          </div>
          <div className="col-md-6">
            <Field label="Preferred channel">
              <select className="form-select" value={form.preferred_channel} onChange={set('preferred_channel')}>
                <option value="">Not set</option>
                {PREFERRED_CHANNEL.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          </div>
        </div>

        <div className="eyebrow mb-2">Buying committee (§10)</div>
        <div className="row g-3 mb-3">
          <div className="col-md-4">
            <Field label="Buying role">
              <select className="form-select" value={form.buying_role} onChange={set('buying_role')}>
                <option value="">Not set</option>
                {BUYING_ROLE.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
          </div>
          <div className="col-md-4">
            <Field label="Influence">
              <select className="form-select" value={form.influence} onChange={set('influence')}>
                <option value="">Not set</option>
                {INFLUENCE.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </Field>
          </div>
          <div className="col-md-4 d-flex align-items-end">
            <div className="form-check">
              <input className="form-check-input" type="checkbox" id="isPrimary" checked={form.is_primary} onChange={set('is_primary')} />
              <label className="form-check-label t-sm" htmlFor="isPrimary">Primary contact at this account</label>
            </div>
          </div>
        </div>

        <div className="eyebrow mb-2">Dates that drive Anniversaries</div>
        <div className="row g-3">
          <div className="col-md-4">
            <Field label="Birthday"><input className="form-control" type="date" value={form.birthday} onChange={set('birthday')} /></Field>
          </div>
          <div className="col-md-4">
            <Field label="Joined company on"><input className="form-control" type="date" value={form.joined_company_on} onChange={set('joined_company_on')} /></Field>
          </div>
          <div className="col-md-4">
            <Field label="Customer since"><input className="form-control" type="date" value={form.customer_since} onChange={set('customer_since')} /></Field>
          </div>
        </div>
      </form>
    </Modal>
  );
}

const Picker = ({ value, onChange, options, blank = 'Not set' }) => (
  <select className="form-select" value={value} onChange={onChange}>
    <option value="">{blank}</option>
    {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);

/** Delete, with the reason the database insists on. */
export function DeleteContactModal({ open, contact, onClose, onConfirm, busy }) {
  const [reason, setReason] = useState('');

  useEffect(() => { if (open) setReason(''); }, [open]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Delete contact"
      footer={(
        <>
          <button type="button" className="btn btn-cancel btn-sm-x" onClick={onClose}>Cancel</button>
          <button
            type="button" className="btn btn-delete btn-sm-x"
            disabled={!reason.trim() || busy} onClick={() => onConfirm(reason.trim())}
          >
            {busy ? <><ButtonSpinner />Deleting…</> : 'Delete'}
          </button>
        </>
      )}
    >
      <p className="t-sm ink-2">
        Deleting <strong>{contact?.fullName}</strong> ({contact?.contactNo}).
        {' '}The row stays in the database with <code>deleted_at</code> set, and
        {' '}<code>POST /contacts/{'{id}'}/restore</code> brings it back.
      </p>
      <Field
        label="Reason" required
        help="Written to the audit trail. delete is requires_reason in iam.app_operation, so the database rejects the write without one."
      >
        <input
          className="form-control" value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Left the company" autoFocus
        />
      </Field>
    </Modal>
  );
}

/** Why a header action is disabled, in a dialog rather than a tooltip — same pattern as leads/accounts. */
export function NotBuiltModal({ open, action, onClose }) {
  if (!action) return null;
  return (
    <Modal
      open={open} onClose={onClose} title={action.title}
      footer={<button type="button" className="btn btn-brand btn-sm-x" onClick={onClose}>Close</button>}
    >
      <p className="t-sm ink-2">{action.why}</p>
      <div className="t-xs ink-3">Endpoint: <code>{action.endpoint}</code> — {action.section}</div>
    </Modal>
  );
}
